import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  Divider,
  FormControl,
  FormLabel,
  Image,
  Input,
  Stack,
  Textarea,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import ImageUploader from '../../common/image-uploader';
import { useDispatch, useSelector } from 'react-redux';
import { setSite } from '../../../features/site/siteSlice';
import { getBackendBaseURL } from '../../../utils/utils';
import { updateSite } from '../../../api/siteApi';

function HeroSection({ isEdit, prevClicked, nextClicked }) {
  const { user } = useSelector(state => state.user);
  const { site } = useSelector(state => state.site);
  const dispatch = useDispatch();
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [src, setSrc] = useState('');
  const [inProgress, setInProgress] = useState(false);
  const [displayNext, setDisplayNext] = useState(isEdit);
  const [actionStatus, setActionStatus] = useState('');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const toast = useToast();

  useEffect(() => {
    if (isEdit) {
      setupComponent();
    }
    // eslint-disable-next-line
  }, []);

  const setupComponent = () => {
    setTitle(site?.heroSection?.title ?? '');
    setSummary(site?.heroSection?.summary ?? '');
    let path = site?.heroSection?.src || '';
    setSrc(path);
  };

  const getPhotoUrl = () => {
    let url = src;
    if (url && !url.startsWith('http')) {
      url = `${getBackendBaseURL()}/${url}`;
    }
    return url;
  };

  const addHeroSectionDetails = () => {
    if (title === '' || summary === '' || src === '') {
      toast({
        title: 'Error',
        description: 'Please set the proper details',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    setInProgress(true);
    let payload_src = src;
    if (payload_src.startsWith(getBackendBaseURL())) {
      payload_src = payload_src.replace(`${getBackendBaseURL()}/`, '');
    }
    let payload = {
      title,
      summary,
      src: payload_src,
      id: site._id,
      creator: user._id,
      updateType: 'HERO_SECTION',
    };

    updateSite(payload)
      .then(response => {
        setInProgress(false);
        if (response.site) {
          if (isEdit) {
            dispatch(setSite(response.site));
          }
          let message = isEdit
            ? 'Updated Hero Section details Successfully.'
            : 'Added Hero Section details, Please add next details.';
          toast({
            title: 'Success',
            description: message,
            status: 'success',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
          setActionStatus(`${message}`);
          setDisplayNext(true);
        } else {
          toast({
            title: 'Error',
            description: response.message,
            status: 'error',
            duration: 5000,
            isClosable: true,
            position: 'top-right',
          });
        }
      })
      .catch(error => {
        setInProgress(false);
        toast({
          title: 'Error',
          description: 'Something went wrong, please try again',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
      });
  };

  return (
    <Card bg={useColorModeValue('white', 'gray.700')}>
      <CardBody>
        <Stack spacing="4" mt="2">
          <FormControl isRequired>
            <FormLabel>Enter Title</FormLabel>
            <Input
              type="text"
              name="text"
              placeholder="Enter the title for hero section"
              defaultValue={title}
              onChange={event => setTitle(event.target.value)}
            />
          </FormControl>
          <FormControl isRequired>
            <FormLabel>Enter Summary</FormLabel>
            <Textarea
              placeholder="Enter the summary for hero section"
              defaultValue={summary}
              onChange={event => setSummary(event.target.value)}
            />
          </FormControl>
          {src !== '' && isEdit ? (
            <Box w="100%">
              <Stack direction="column" spacing={4} mb={4}>
                <Box flexShrink={0}>
                  <Image
                    src={getPhotoUrl()}
                    alt="Hero Photo"
                    maxH="100px"
                    objectFit="contain"
                    borderRadius="md"
                    border="1px solid"
                    borderColor={borderColor}
                    p={2}
                  />
                </Box>
                <FormControl>
                  <ImageUploader
                    label={'Change Photo'}
                    OnUpload={path => setSrc(path)}
                  />
                </FormControl>
              </Stack>
            </Box>
          ) : (
            <FormControl isRequired>
              <FormLabel>Select photo</FormLabel>
              <ImageUploader
                label={'Select Photo'}
                OnUpload={path => setSrc(path)}
              />
            </FormControl>
          )}
        </Stack>
      </CardBody>
      <Divider />
      <CardFooter>
        <ButtonGroup w={'100%'} justifyContent="end">
          <Button colorScheme="teal" size="md" onClick={prevClicked}>
            Previous
          </Button>
          <Button
            colorScheme="teal"
            isLoading={inProgress}
            loadingText="Submitting..."
            disabled={inProgress || actionStatus}
            size="md"
            onClick={addHeroSectionDetails}
            whiteSpace="normal"
          >
            {isEdit ? 'Update' : 'Add'}
          </Button>
          <Button
            colorScheme="teal"
            size="md"
            style={{ display: displayNext ? 'inherit' : 'none' }}
            onClick={nextClicked}
          >
            Next
          </Button>
        </ButtonGroup>
      </CardFooter>
    </Card>
  );
}

export default HeroSection;
